/**
 * @component ImgViewer
 * @description
 * 게시글 본문 이미지 확대 보기(Viewer) 컴포넌트
 * - 게시글 내 이미지 클릭 시 오버레이 형태로 표시
 * - 이전 / 다음 이미지 이동 기능
 * - 이미지 확대 / 축소 기능
 * - 이미지 회전 기능
 * - 전체화면 보기 기능
 * - 마우스 휠로 확대 / 축소 지원
 * - 썸네일 목록을 통한 이미지 선택
 * - "이미지 위치로 글 이동" 기능 제공
 *
 * @props
 * - title : 게시글 제목 (뷰어 상단 표시)
 * - images : 게시글에 포함된 이미지 URL 배열
 * - initialIndex : 최초 표시할 이미지 index
 * - closeViewer : 이미지 뷰어 닫기 함수
 *
 * @state
 * - currentIndex : 현재 표시 중인 이미지 index
 * - zoom : 이미지 확대 배율
 * - rotation : 이미지 회전 각도
 * - isHovered : 이미지 영역 hover 여부 (휠 zoom 활성화 조건)
 *
 * @refs
 * - imgRef : 현재 표시된 이미지 DOM 참조
 *
 * @hooks
 * - useEffect (1) : 이미지 클릭 또는 터치 시 fullscreen 상태 해제
 * - useEffect (2) : fullscreen 종료 시 zoom 초기화
 * - useEffect (3) : 이미지 영역에서 마우스 휠 이벤트로 zoom 제어
 *
 * @functions
 * - handleAction : 이미지 뷰어 이벤트 통합 처리
 *   - PREV : 이전 이미지
 *   - NEXT : 다음 이미지
 *   - ZOOM_IN : 이미지 확대
 *   - ZOOM_OUT : 이미지 축소
 *   - ROTATE : 이미지 시계 방향 회전
 *   - CLOSE : 뷰어 닫기
 *   - FULLSCREEN : 전체 화면 전환
 *   - GO_DETAIL : 해당 이미지가 위치한 본문으로 이동
 *   - SELECT_IMAGE : 썸네일 선택 이미지 표시
 *
 * - goDetail
 *   현재 이미지 위치가 포함된 게시글 본문 위치로 스크롤 이동 후 뷰어 닫기
 *
 * @ui
 * - img-viewer-header : 제목 + 닫기 버튼
 * - img-viewer-content : 메인 이미지 영역
 * - toolbar : 확대 / 축소 / 회전 / 전체화면 / 위치 이동 버튼
 * - img-list : 이미지 썸네일 목록
 * 
 * @since 
 * @author 개발팀
 * @history
 * - 2026-03-12 : 주석 추가, 코드 재배치, 각 버튼 핸들러들 handleAction으로 통합
 */

import React, { useState, useEffect, useRef } from 'react';
import "../../css/viewer.css";

const ImgViewer = ({ title, images, initialIndex, closeViewer }) => {

  //#region 1. State 정의
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  //#endregion

  //#region 2. Refs 정의
  const imgRef = useRef(null);
  //#endregion

  //#region 3. Hooks (useEffect)
  useEffect(() => {
    const handleClick = () => {
      if (document.fullscreenElement) {
        document.exitFullscreen();
      }
    };

    const img = imgRef.current;
    if (img) {
      img.addEventListener("click", handleClick);
      img.addEventListener("touchstart", handleClick);
    }

    return () => {
      if (img) {
        img.removeEventListener("click", handleClick);
        img.removeEventListener("touchstart", handleClick);
      }
    };
  }, []);

   useEffect(() => {
    const exitHandler = () => {
      if (!document.fullscreenElement) {
        setZoom(1);
      }
    };

    document.addEventListener('fullscreenchange', exitHandler);
    return () => {
      document.removeEventListener('fullscreenchange', exitHandler);
    };
  }, []);

  useEffect(() => {
    const imgContainer = document.querySelector('.img-viewer-content');
    if (!imgContainer) return;
  
    const wheelHandler = (e) => {
      if (!isHovered) return;
      e.preventDefault();
      setZoom((prevZoom) => {
        const newZoom = prevZoom + e.deltaY * -0.01;
        return Math.min(Math.max(newZoom, 0.5), 3);
      });
    };
  
    imgContainer.addEventListener('wheel', wheelHandler, { passive: false });
  
    return () => {
      imgContainer.removeEventListener('wheel', wheelHandler);
    };
  }, [isHovered]);
  //#endregion

  //#region 4. Event Handlers
  const handleAction = (type, payload, e) => {
    if (e) e.stopPropagation();
  
    switch (type) {
      case "PREV":
        setCurrentIndex(prev => (prev > 0 ? prev - 1 : images.length - 1));
        setZoom(1);
        break;
  
      case "NEXT":
        setCurrentIndex(prev => (prev < images.length - 1 ? prev + 1 : 0));
        setZoom(1);
        break;
  
      case "ZOOM_IN":
        setZoom(prev => prev * 1.1);
        break;
  
      case "ZOOM_OUT":
        setZoom(prev => prev / 1.1);
        break;
  
      case "ROTATE":
        setRotation(prev => prev + 90);
        break;
  
      case "CLOSE":
        closeViewer(true);
        break;
  
      case "FULLSCREEN":
        if (!document.fullscreenElement) {
          imgRef.current.requestFullscreen();
          setRotation(0);
        } else {
          document.exitFullscreen();
        }
        break;
  
      case "GO_DETAIL":
        goDetail();
        break;
  
      case "SELECT_IMAGE":
        setCurrentIndex(payload);
        break;
  
      default:
        break;
    }
  };
  //#endregion

  //#region 5. Functions (호출되는 순서대로)
  const goDetail = () => {
    const imageId = `image-${currentIndex}`;
    const element = document.getElementById(imageId);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
      setTimeout(() => {
        const element = document.getElementById(imageId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        closeViewer(true);
      }, 100); 
    } else {
      closeViewer(true);
    }
  };
  //#endregion

  //#region 7. Main Render (UI)
  return (
    <div className="img-viewer">
      <div className="img-viewer-header">
        <div className='content-title'>{title}</div>
        <button className="img-icon img-close" onClick={()=>handleAction("CLOSE")} >
          <span className="sound_only">이미지 닫기</span>
        </button>
      </div>
      <p className='img-counter'>{currentIndex + 1} / {images.length}</p>
      <div className="img-viewer-content" 
        onMouseEnter={() => setIsHovered(true)} 
        onMouseLeave={() => setIsHovered(false)} 
        onClick={()=>handleAction("CLOSE")}
      >
        <img
          ref={imgRef}
          src={images[currentIndex]}
          alt="현재 이미지"
          style={{
            transform: `rotate(${rotation}deg) scale(${zoom})`, 
            transition: "transform 0.3s", 
          }}
          onClick={(e) => {e.stopPropagation();}}
        />
        <button className="img-icon img-left" onClick={(e)=>handleAction("PREV",null,e)}><span className="sound_only">이전 이미지</span></button>
        <button className="img-icon img-right" onClick={(e)=>handleAction("NEXT",null,e)}><span className="sound_only">이후 이미지</span></button>
      </div>
      <div className="toolbar">
        <button className="img-icon zoom-in" onClick={()=>handleAction("ZOOM_IN")}><span className="sound_only">이미지 확대</span></button>
        <button className="img-icon zoom-out" onClick={()=>handleAction("ZOOM_OUT")}><span className="sound_only">이미지 축소</span></button>
        <button className="img-icon full-screen" onClick={()=>handleAction("FULLSCREEN")}><span className="sound_only">이미지 전체보기</span></button>
        <button className="img-icon img-clockwise" onClick={()=>handleAction("ROTATE")}><span className="sound_only">이미지 시계방향 회전</span></button>
        <button className="img-icon content-text" onClick={()=>handleAction("GO_DETAIL")}><span className="sound_only">이미지 위치로 글 이동</span></button>
      </div>
      <div className="img-list">
        <h3 className="sound_only">이미지 리스트</h3>
        {images.map((image, index) => (
          <img
            key={index}
            src={image}
            alt={`이미지 ${index + 1}`}
            onClick={() => handleAction("SELECT_IMAGE", index)}
            className={index === currentIndex ? 'active' : ''}
          />
        ))}
      </div>
    </div>
  );  
  //#endregion
};

export default ImgViewer;
